/**
 * The helpers in this module speak epoch milliseconds (`number`) at their
 * boundaries, then read the clock and format through these two helpers so the
 * whole module shares one clock and one formatting path.
 *
 * Clock-source note: this is the single place the repo funnels time access
 * through. We previously read the clock via `Temporal` (`temporal-polyfill`),
 * but the polyfill fails to resolve under Node 24 + ESM
 * (`ERR_PACKAGE_PATH_NOT_EXPORTED`), so the Temporal migration is paused and
 * these helpers use the standard `Date` API. When we move to Node 26 (which
 * ships `Temporal` natively) this module is the one place to switch back. See
 * the "Time and dates" guidance in the `AGENTS.md` guides.
 */
function nowMs(): number {
  return Date.now();
}

function instant(ts: number): Date {
  return new Date(ts);
}

/**
 * Relative time formatting utilities.
 *
 * Three modes:
 * - **full** (default): "just now", "5m ago", "2h ago", "3d ago"
 * - **compact**: "Now", "5m", "2h", "3d"
 * - **short**: "0m", "5m", "2h", "3d", "3mo" — includes month-level granularity
 */
export function timeAgo(ts: number, opts?: { compact?: boolean; short?: boolean }): string {
  const diff = nowMs() - ts;

  if (opts?.short) {
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m`;
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h`;
    if (diff < 2_592_000_000) return `${Math.floor(diff / 86_400_000)}d`;
    return `${Math.floor(diff / 2_592_000_000)}mo`;
  }

  const seconds = Math.floor(diff / 1000);

  if (opts?.compact) {
    if (seconds < 60) return 'Now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h`;
    return `${Math.floor(hours / 24)}d`;
  }

  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

const RELATIVE_TIME_UNITS: ReadonlyArray<{ unit: Intl.RelativeTimeFormatUnit; ms: number }> = [
  { unit: 'year', ms: 31_536_000_000 },
  { unit: 'month', ms: 2_592_000_000 },
  { unit: 'day', ms: 86_400_000 },
  { unit: 'hour', ms: 3_600_000 },
  { unit: 'minute', ms: 60_000 },
  { unit: 'second', ms: 1_000 },
];

const relativeTimeFormatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

/**
 * Localized relative time for a timestamp in either direction, backed by the
 * platform `Intl.RelativeTimeFormat`. Unlike {@link timeAgo} (which only counts
 * elapsed time and renders a nonsensical negative "-20160m" for future dates),
 * this handles forward-looking timestamps correctly: future → "in 14 days",
 * past → "5 minutes ago", within a second of now → "now". The largest sensible
 * unit is chosen automatically; the runtime handles pluralization and locale.
 */
export function formatRelativeTime(ts: number): string {
  const diffMs = ts - nowMs();
  for (const { unit, ms } of RELATIVE_TIME_UNITS) {
    const value = Math.trunc(diffMs / ms);
    if (value !== 0 || unit === 'second') {
      return relativeTimeFormatter.format(value, unit);
    }
  }
  return relativeTimeFormatter.format(0, 'second');
}

/** "Jan 5, 2024" — date-only, with year. */
export function formatDateOnly(ts: number): string {
  return instant(ts).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** "January 5, 2024" — long-form date, with year. */
export function formatDateLong(ts: number): string {
  return instant(ts).toLocaleString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/** Truncate a string to `max` characters, appending an ellipsis if needed. */
export function truncate(s: string, max: number): string {
  return s.length > max ? s.slice(0, max) + '\u2026' : s;
}

/** Uppercase the first character of a string and leave the rest unchanged. Empty strings are returned as-is. */
export function capitalize(s: string): string {
  return s.length === 0 ? s : s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Known acronyms / branded casings used when title-casing a kebab-case
 * identifier. Lowercase key → preferred display casing.
 */
const PLUGIN_NAME_ACRONYMS: Record<string, string> = {
  ai: 'AI',
  api: 'API',
  db: 'DB',
  github: 'GitHub',
  gitlab: 'GitLab',
  id: 'ID',
  mcp: 'MCP',
  oauth: 'OAuth',
  pr: 'PR',
  sso: 'SSO',
  ui: 'UI',
  url: 'URL',
};

/**
 * Display label for a kebab-case plugin name. Splits on hyphens, title-cases
 * each word, and substitutes known acronyms (e.g. `"github"` → `"GitHub"`,
 * `"ai-gateway"` → `"AI Gateway"`, `"axon-viewer"` → `"Axon Viewer"`).
 * Empty input is returned as-is.
 */
export function formatPluginName(name: string): string {
  if (name.length === 0) return name;
  return name
    .split('-')
    .map((segment) => {
      if (segment.length === 0) return segment;
      const lower = segment.toLowerCase();
      return PLUGIN_NAME_ACRONYMS[lower] ?? lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ');
}

/** Truncate a UUID-style ID to "abc123…ef01". */
export function truncateId(id: string): string {
  return id.length > 12 ? `${id.slice(0, 6)}\u2026${id.slice(-4)}` : id;
}

/**
 * Display label for a bare provider name (e.g. `"github"`, `"google"`).
 * Use this with values from `provider_accounts.provider`.
 */
export function getProviderName(provider: string): string {
  switch (provider) {
    case 'github':
      return 'GitHub';
    case 'google':
      return 'Google';
    case 'slack':
      return 'Slack';
    case 'linear':
      return 'Linear';
    default:
      return provider;
  }
}

/**
 * Display label for a composite provider id (e.g. `"github:42"`).
 * Use this with `User.providerId`. Tolerates nullish input (e.g. legacy
 * users persisted before the field became required) and renders a stable
 * "Unknown" label rather than crashing the surrounding page.
 */
export function getProviderLabel(providerId: string | null | undefined): string {
  if (!providerId) return 'Unknown';
  return getProviderName(providerId.split(':')[0] ?? providerId);
}

/**
 * Format a byte count as a human-readable size ("512 MB", "1.4 GB"). Uses
 * binary (1024) units and drops the decimal for whole-unit or >=100 values so
 * compact rows stay tidy. Returns "0 B" for non-finite or non-positive input.
 */
export function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
}

/**
 * Format a token count compactly for dense readouts ("512", "12K", "1.5K",
 * "1.2M"). Keeps one decimal for sub-10K thousands and sub-10M millions so
 * small values stay legible, drops it otherwise. Returns "0" for non-finite or
 * negative input.
 */
export function formatTokenCount(n: number): string {
  if (!Number.isFinite(n) || n < 0) return '0';
  if (n < 1_000) return `${Math.round(n)}`;
  if (n < 1_000_000) {
    const v = n / 1_000;
    return `${v.toFixed(v >= 10 ? 0 : 1)}K`;
  }
  const v = n / 1_000_000;
  return `${v.toFixed(v >= 10 ? 0 : 1)}M`;
}

/**
 * Decimal-place rules for a USD amount so cheap calls don't round to `$0.00`
 * and look like a bug. Shared by the string formatter ({@link formatUsd}) and
 * any animated figure that needs the same thresholds as an `Intl.NumberFormat`
 * options object (e.g. NumberFlow's `format` prop).
 *
 * - `abs >= 1000` → whole dollars (`$1,287`)
 * - `abs >= 1` → two decimals (`$21.47`)
 * - `abs >= 0.01` → up to three decimals (`$0.123`)
 * - below that → four decimals (`$0.0012`) so a single mini turn still surfaces
 *   a non-zero number
 *
 * Returns `Omit<Intl.NumberFormatOptions, 'notation'>` (the options never set
 * `notation`) so the result also satisfies number-flow's narrower `Format`
 * type when used as a NumberFlow `format` prop — without forcing this pure
 * helper to depend on `@number-flow/react`.
 */
export function usdFractionOptions(value: number): Omit<Intl.NumberFormatOptions, 'notation'> {
  const abs = Math.abs(value);
  if (abs >= 1000) return { maximumFractionDigits: 0 };
  if (abs >= 1) return { minimumFractionDigits: 2, maximumFractionDigits: 2 };
  if (abs >= 0.01) return { minimumFractionDigits: 2, maximumFractionDigits: 3 };
  if (abs > 0) return { minimumFractionDigits: 4, maximumFractionDigits: 4 };
  return { minimumFractionDigits: 2, maximumFractionDigits: 2 };
}

/**
 * Format a USD amount for a dense admin table. Values under `$0.01` are shown
 * to four decimals so cheap calls (a single mini turn) still surface a non-zero
 * number rather than rounding to `$0.00`. Returns `"$0.00"` for non-finite input
 * and for zero (including negative zero, which `Intl` would otherwise render
 * as `$-0.00`). Uses {@link usdFractionOptions} so the rendered string stays
 * identical to any animated figure that formats through the same threshold
 * ladder.
 */
export function formatUsd(n: number): string {
  if (!Number.isFinite(n) || n === 0) return '$0.00';
  return `$${new Intl.NumberFormat('en-US', usdFractionOptions(n)).format(n)}`;
}

/**
 * Format a CPU core count as a human-readable amount ("1.6 cores", "0.25
 * cores", "1 core"). Whole values drop the decimal; sub-core values keep two
 * places so a small slice doesn't read as "0 cores". Returns "0 cores" for
 * non-finite or non-positive input.
 */
export function formatCores(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '0 cores';
  const value = n >= 1 ? n.toFixed(n % 1 === 0 ? 0 : 1) : n.toFixed(2);
  return `${value} ${value === '1' ? 'core' : 'cores'}`;
}

/** Format a millisecond duration as "Xm Ys" or "Xs". Returns "<1s" for durations under 1 second. */
export function formatElapsedMs(ms: number): string {
  if (ms < 1000) return '<1s';
  const totalSeconds = Math.floor(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${seconds}s`;
}

/** Format a nullable millisecond duration as "Xh Ym", "Xm Ys", or "Xs". Returns "—" for null/undefined. */
export function formatDuration(ms: number | null | undefined): string {
  if (ms == null) return '—';
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remaining = seconds % 60;
  if (minutes < 60) return `${minutes}m ${remaining}s`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
}

/**
 * Like {@link formatDuration} but keeps hundredths-of-a-second precision for
 * sub-minute spans (e.g. "3.00s", "0.31s", "8.42s"). Used where short
 * durations need finer granularity than whole seconds — e.g. devbox setup
 * timing. Spans of a minute or more fall back to the coarse "Xm Ys" / "Xh Ym".
 */
export function formatDurationPrecise(ms: number | null | undefined): string {
  if (ms == null) return '—';
  if (ms < 60_000) return `${(ms / 1000).toFixed(2)}s`;
  return formatDuration(ms);
}

/** "Jan 5, 09:30 AM" — short date+time for timestamps. Returns "—" for null/undefined. */
export function formatDateTime(ms: number | null | undefined): string {
  if (ms == null) return '—';
  return instant(ms).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Format a time range (start→end) as an elapsed duration string. Uses the current time when `end` is absent. */
export function formatDurationRange(start: number, end?: number | null): string {
  return formatElapsedMs((end ?? nowMs()) - start);
}

/** "09:30:15" — time-only with seconds. */
export function formatTime(ts: number): string {
  return instant(ts).toLocaleString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/** "Jan 5, 09:30:15 AM" — short date+time with seconds. */
export function formatDateTimeWithSeconds(ts: number): string {
  return instant(ts).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/** "Jan 5, 2024, 09:30:15.123 AM" — full timestamp with milliseconds. */
export function formatFullTimestamp(ts: number): string {
  return instant(ts).toLocaleString([], {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    fractionalSecondDigits: 3,
  });
}
